import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';

import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import {
    ManagePinModalComponent,
    type ManagePinModalData,
} from '@connect-services/client/components/modals/manage-pin-modal/manage-pin-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { Pin } from '@connect-services/client/sdk/api';
import { PinService } from '@connect-services/client/services/pin.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

enum PinSortOrder {
    ALPHABETICAL = 'alphabetical',
    MOST_RECENT = 'mostRecent',
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'absolute inset-0 z-[45] overflow-hidden pointer-events-none' },
    imports: [ButtonComponent, RessourcesModule],
    selector: 'app-pin-panel',
    templateUrl: './pin-panel.component.html',
})
export class PinPanelComponent {
    protected readonly ButtonType = ButtonType;
    protected readonly canModify = computed<(pin: Pin) => boolean>(() => {
        const currentPersonId = this.securityService.meSignal()?.id;
        const canManage = this.pinService.canManage();

        return (pin: Pin): boolean => canManage && currentPersonId === pin.createdBy;
    });
    protected readonly pinService = inject(PinService);
    protected readonly PinSortOrder = PinSortOrder;
    protected readonly search = signal<string>('');
    protected readonly sortOrder = signal<PinSortOrder>(PinSortOrder.ALPHABETICAL);
    protected readonly visiblePins = computed<Pin[]>(() => {
        const searchValue = this.search().trim().toLocaleLowerCase();
        const pins = this.pinService
            .pins()
            .filter((pin: Pin) => !searchValue || pin.name.toLocaleLowerCase().includes(searchValue));

        return [...pins].sort((firstPin: Pin, secondPin: Pin) => {
            if (this.sortOrder() === PinSortOrder.MOST_RECENT) {
                return new Date(secondPin.createdAt).getTime() - new Date(firstPin.createdAt).getTime();
            }

            return firstPin.name.localeCompare(secondPin.name, undefined, { numeric: true, sensitivity: 'base' });
        });
    });

    private editDialogRef?: MatDialogRef<ManagePinModalComponent>;
    private editedPinId?: string;
    private readonly matDialog = inject(MatDialog);
    private readonly securityService = inject(SecurityService);
    private readonly translocoService = inject(TranslocoService);

    protected confirmDelete(pin: Pin): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('delete'),
                description: this.translocoService.translate('pin.deleteConfirmation', { name: pin.name }),
                title: this.translocoService.translate('warning'),
                type: ModalType.DANGER,
            },
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result === ModalResultEnum.CONFIRM) {
                await this.pinService.delete(pin.id);

                if (this.editedPinId === pin.id) {
                    this.editDialogRef?.close();
                }
            }
        });
    }

    protected edit(pin: Pin): void {
        if (this.editDialogRef) {
            if (this.editDialogRef.componentInstance.setPin(pin)) {
                this.editedPinId = pin.id;
            }

            return;
        }

        this.editedPinId = pin.id;

        const dialogRef = this.matDialog.open<ManagePinModalComponent, ManagePinModalData, void>(
            ManagePinModalComponent,
            {
                autoFocus: false,
                data: { pin },
                hasBackdrop: false,
                panelClass: 'pin-create-dialog',
                width: '48rem',
            },
        );

        this.editDialogRef = dialogRef;
        dialogRef.afterClosed().subscribe(() => {
            if (this.editDialogRef === dialogRef) {
                this.editDialogRef = undefined;
                this.editedPinId = undefined;
            }
        });
    }

    protected updateSearch(value: string): void {
        this.search.set(value);
    }

    protected updateSortOrder(sortOrder: PinSortOrder): void {
        this.sortOrder.set(sortOrder);
    }

    protected zoomTo(pin: Pin): void {
        this.pinService.zoomTo(pin);
    }
}
